/** Всплывающие карточки ячеек «Остаток ФБС» и «В пути» — ЧИСТЫЕ модели без React.
 *
 *  Карточка остатка строится на той же разбивке, что и «почему» (`fbsStock.stockBreakdown`):
 *  своя копия правил разошлась бы с ней. Отличаются только подписи — в карточке они длиннее и
 *  человечнее. Не сошлась разбивка с числом в колонке — карточка говорит это словами
 *  (`mismatch`), а не подгоняет итог.
 */
import type { FbsStockDetail, FbsTransitDoc } from "@/api/supply";
import { NEGATIVE_STOCK_HINT, fmtClock, stockBreakdown } from "./fbsStock";
import { fmt, fmtDate } from "./format";

/** Что карточке нужно от отчёта (а не от строки). */
export interface FbsCardCtx {
  /** Имя склада 1С (комнаты ФБС) — `FbsReport.warehouse.target_wh_name`. */
  whName: string;
  /** На когда «Остаток ФБС» — `FbsReport.stock_as_of`. */
  stockAsOf: string;
  /** Давность «в пути» (горизонт застрявших), дней — `FbsReport.params.stale_days`. */
  staleDays: number;
}

export interface CardLine {
  key: string;
  label: string;
  /** Пояснение мелким шрифтом под подписью (например, «имя не известно»). */
  note?: string;
  /** Полная подсказка (ref юрлица). */
  title?: string;
  sign: "+" | "−";
  qty: number;
  /** Приглушить (юрлицо, у которого ничего нет). */
  muted: boolean;
}

export interface StockCardModel {
  title: string;
  lines: CardLine[];
  /** Итог «= свободно» — у wh-helper (у 1С слагаемое одно, итог повторял бы его). */
  total: number | null;
  negativeHint: string | null;
  /** Разбивка не сошлась с числом в колонке: текст для показа, иначе null. */
  mismatch: string | null;
  footer: string;
}

/** Короткий ref юрлица для подписи — полная ссылка уходит в подсказку. */
export const shortRef = (ref: string): string => (ref.length > 8 ? `${ref.slice(0, 8)}…` : ref);

const CARD_MINUS_LABEL: Record<"in_flight" | "wb" | "ozon", string> = {
  in_flight: "собрано, но не проведено в 1С",
  wb: "несобранные задания WB",
  ozon: "несобранные отправления Ozon",
};

export function stockCard(
  detail: FbsStockDetail,
  stockQty: number,
  ctx: Pick<FbsCardCtx, "whName" | "stockAsOf">,
): StockCardModel {
  const b = stockBreakdown(detail, stockQty);
  const lines: CardLine[] = b.terms.map((t) => {
    if (t.kind !== "org")
      return { key: t.key, label: CARD_MINUS_LABEL[t.kind], sign: "−", qty: t.qty, muted: false };
    const ref = t.ref ?? "";
    const known = t.name !== null && t.name !== undefined;
    return {
      key: t.key,
      label: known ? (t.name as string) : shortRef(ref),
      note: known ? undefined : "имя не известно",
      title: ref,
      sign: "+",
      qty: t.qty,
      muted: t.qty === 0,
    };
  });
  const wh = detail.source === "wh_helper";
  return {
    title: `Остаток ФБС — ${ctx.whName}`,
    lines,
    total: wh ? stockQty : null,
    negativeHint: stockQty < 0 ? NEGATIVE_STOCK_HINT.toLowerCase() : null,
    mismatch: b.mismatch
      ? `Разбивка (${fmt(b.sum)}) не сходится с остатком (${fmt(stockQty)}) — ошибка расчёта, сообщите разработчику.`
      : null,
    footer: wh
      ? `тот же расчёт, что синк wh-helper публикует в WB · данные на ${fmtClock(ctx.stockAsOf)}`
      : "свободный остаток 1С организации склада",
  };
}

export interface TransitCardModel {
  title: string;
  lines: { key: string; label: string; qty: number }[];
  total: number;
  mismatch: string | null;
  footer: string;
}

/** «В пути» по заказам на перемещение: номер, дата, состояние, штук; итог — число колонки. */
export function transitCard(
  docs: readonly FbsTransitDoc[],
  transitQty: number,
  ctx: Pick<FbsCardCtx, "whName" | "staleDays">,
): TransitCardModel {
  const sum = docs.reduce((s, d) => s + d.qty, 0);
  return {
    title: `В пути — ${ctx.whName}`,
    lines: docs.map((d) => ({
      key: `${d.number}|${d.date}`,
      label: `${d.number} от ${fmtDate(d.date)} · ${d.state}`,
      qty: d.qty,
    })),
    total: transitQty,
    mismatch:
      Math.abs(sum - transitQty) > 1e-6
        ? `Заказы (${fmt(sum)}) не сходятся с «в пути» (${fmt(transitQty)}) — ошибка расчёта, сообщите разработчику.`
        : null,
    footer:
      "проведённые заказы на перемещение на склад ФБС в состоянии «В работе»/«К отгрузке», " +
      `не старше ${ctx.staleDays} дн. (горизонт застрявших); застрявшие и непроведённые — ` +
      "в предупреждениях расчёта",
  };
}
